import { createApi } from "@reduxjs/toolkit/query/react";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { EmployeeInterface } from "../interfaces/EmployeeInterface";
import { MetaInterface } from "../interfaces/MetaInterface";
import { ApiEnum } from "../enums/ApiEnum";
import { createQueryString } from "../utils/createQueryString";

export const employeeApiSlice = createApi({
    reducerPath: "employeeApiSlice",
    baseQuery: apiBaseQuery(),
    tagTypes: ["Employee"],
    endpoints: (builder) => ({
        getEmployees: builder.query<
            { data: EmployeeInterface[]; meta?: MetaInterface },
            {
                page?: number;
                items_per_page?: number;
                sort?: string;
                filter?: {
                    status?: string;
                    search?: string;
                    qualification_id?: number;
                    establishment_id?: number;
                    missing_qualification_id?: number;
                };
            }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}?${createQueryString(values)}`,
                    method: "GET",
                };
            },
            providesTags: ["Employee"],
        }),
        getEmployeeById: builder.query<EmployeeInterface, { id: number }>({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}`,
                    method: "GET",
                };
            },
            providesTags: ["Employee"],
        }),
        getEmployeeMe: builder.query<EmployeeInterface, object>({
            query: () => ({
                url: `${ApiEnum.EMPLOYEE}/me`,
                method: "GET",
            }),
        }),
        resendInvite: builder.mutation<
            EmployeeInterface,
            {
                id: number;
                data: {
                    establishment_id: number;
                };
            }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/resend-invite?filter[establishment_id]=${values.data.establishment_id}`,
                    method: "POST",
                };
            },
            invalidatesTags: ["Employee"],
        }),
        activate: builder.mutation<
            EmployeeInterface,
            {
                id: number;
                data: {
                    establishment_id: number;
                };
            }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/activate?filter[establishment_id]=${values.data.establishment_id}`,
                    method: "POST",
                };
            },
            invalidatesTags: ["Employee"],
        }),
        deactivate: builder.mutation<
            EmployeeInterface,
            {
                id: number;
                data: {
                    establishment_id: number;
                    out_of_service_date: string;
                };
            }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/deactivate?filter[establishment_id]=${values.data.establishment_id}`,
                    method: "POST",
                    body: {
                        out_of_service_date: values.data.out_of_service_date,
                    },
                    responseHandler: "text",
                } as const;
            },
            invalidatesTags: ["Employee"],
        }),
        changeEmail: builder.mutation<
            EmployeeInterface,
            { id: number; newEmail: string }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/change-email`,
                    method: "POST",
                    body: { new_email: values.newEmail },
                };
            },
            invalidatesTags: ["Employee"],
        }),
        cancelChangeEmail: builder.mutation<EmployeeInterface, { id: number }>({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/cancel-change-email`,
                    method: "POST",
                };
            },
            invalidatesTags: (_result, _error, { id }) => [
                { type: "Employee" as const, id },
            ],
        }),
        resendChangeEmail: builder.mutation<EmployeeInterface, { id: number }>({
            query: (values) => {
                return {
                    url: `${ApiEnum.EMPLOYEE}/${values.id}/resend-change-email`,
                    method: "POST",
                };
            },
            invalidatesTags: ["Employee"],
        }),
        createEmployee: builder.mutation<
            { data: EmployeeInterface },
            {
                data: object;
            }
        >({
            query: (values) => ({
                url: ApiEnum.EMPLOYEE,
                method: "POST",
                body: values.data,
            }),
            invalidatesTags: [{ type: "Employee" as const, id: "LIST" }],
        }),
        patchEmployee: builder.mutation<
            { data: EmployeeInterface },
            {
                id: number;
                data: object;
            }
        >({
            query: (values) => ({
                url: ApiEnum.EMPLOYEE + `/${values.id}`,
                method: "PATCH",
                body: values.data,
            }),
            invalidatesTags: ["Employee"],
        }),
        deleteEmployee: builder.mutation<
            { data: EmployeeInterface },
            { id: number }
        >({
            query: (values) => ({
                url: ApiEnum.EMPLOYEE + `/${values.id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Employee"],
        }),
    }),
});

export default employeeApiSlice.reducer;

export const {
    useGetEmployeesQuery,
    useGetEmployeeByIdQuery,
    useGetEmployeeMeQuery,
    useCreateEmployeeMutation,
    usePatchEmployeeMutation,
    useDeleteEmployeeMutation,
    useResendInviteMutation,
    useActivateMutation,
    useDeactivateMutation,
    useChangeEmailMutation,
    useCancelChangeEmailMutation,
    useResendChangeEmailMutation,
} = employeeApiSlice;
